import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../components/Common/BackButton';
import { HowDidYouFindUsType } from '../../../interfaces/teacher.interface';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';
import { RootState } from '../../../store/store';
import { setTeacherOnboarding, setUserOnboarding } from '../../../store/userSlice';
import { saveTeacherSurvey } from '../services/Teacher.service';
import { TeacherOnboardingPagesEnum } from '../TeacherOnboarding';

const HOW_FOUND_OPTIONS = [
  { value: HowDidYouFindUsType.Instagram, label: 'Instagram' },
  { value: HowDidYouFindUsType.FacebookGroup, label: 'Facebook group' },
  { value: HowDidYouFindUsType.GoogleSearch, label: 'Google search' },
  { value: HowDidYouFindUsType.Email, label: 'Email' },
  { value: HowDidYouFindUsType.Colleague, label: 'Colleague' },
  { value: HowDidYouFindUsType.Conference, label: 'Conference' },
  { value: HowDidYouFindUsType.Other, label: 'Other' },
];

export interface OnboardingHowDidYouFindUsProps {
  onSuccess?: () => void;
  onBack?: () => void;
}

const OnboardingHowDidYouFindUs: React.FC<OnboardingHowDidYouFindUsProps> = ({ onSuccess, onBack }) => {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const [exitAnimation, setExitAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [hasReferralCode, setHasReferralCode] = useState<boolean>(!!teacherOnboarding?.referralCode);
  const [referralCode, setReferralCode] = useState<string>(teacherOnboarding?.referralCode || '');
  const [howFoundUs, setHowFoundUs] = useState<HowDidYouFindUsType | null>(teacherOnboarding?.howFoundUs || null);
  const [howFoundUsOther, setHowFoundUsOther] = useState<string>(teacherOnboarding?.howFoundUsOther || '');

  // Reset exit animation on component mount
  React.useEffect(() => {
    setExitAnimation(false);
  }, []);

  // UI state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBack = () => {
    setExitAnimation(true);
    setTimeout(() => {
      if (onBack) onBack();
      else navigateTo(TeacherOnboardingPagesEnum.OnboardingClassInfo);
    }, 250);
  };

  const handleContinue = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const data = {
      referralCode: hasReferralCode ? referralCode.trim() : undefined,
      howFoundUs: howFoundUs || undefined,
      howFoundUsOther: howFoundUs === HowDidYouFindUsType.Other ? howFoundUsOther.trim() : undefined,
      completedAt: new Date(),
    };
    dispatch(setTeacherOnboarding({ data }));

    try {
      await saveTeacherSurvey({ ...teacherOnboarding, ...data }, () => {
        dispatch(setUserOnboarding({ data: { ...teacherOnboarding, ...data } }));
        localStorage.removeItem(UserLocalStorageKeysEnum.TEACHER_DRAFT_FORM);

        setExitAnimation(true);
        setTimeout(() => {
          if (onSuccess) onSuccess();
          else navigateTo('/classroom/dashboard');
        }, 250);
      }, undefined, true);
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const selectedHowFoundOption = HOW_FOUND_OPTIONS.find((option) => option.value === howFoundUs);
  const isFormValid = true;

  return (
    <div
      className={`max-w-full mx-auto px-4 py-6 sm:py-8 transition-all duration-500 ${exitAnimation ? 'opacity-0 transform translate-y-10' : 'opacity-100'}`}
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-6 text-left">
        {/* Referral code (optional) */}
        <div className="relative">
          <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
            Have a referral code? <span className="font-normal text-secondaryStoryGrayButtonText">(optional)</span>
          </h2>
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="hasReferralCode"
                checked={hasReferralCode === true}
                onChange={() => {
                  setHasReferralCode(true);
                  if (!referralCode) setReferralCode('');
                }}
                className="accent-secondaryStoryAdditionalLightBlueText"
              />
              <span>Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="hasReferralCode"
                checked={hasReferralCode === false}
                onChange={() => {
                  setHasReferralCode(false);
                  setReferralCode('');
                }}
                className="accent-secondaryStoryAdditionalLightBlueText"
              />
              <span>No</span>
            </label>
          </div>
          {hasReferralCode && (
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter referral code"
              className="w-full mt-2 p-3 text-center border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="Referral code"
            />
          )}
        </div>

        {/* Where did you hear about us (optional) */}
        <div className="relative">
          <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
            Where did you hear about us?{' '}
            <span className="font-normal text-secondaryStoryGrayButtonText">(optional)</span>
          </h2>

          <div className="relative" ref={dropdownRef}>
            <div
              className="w-full p-3 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between text-center"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="w-full text-center">
                <span className={`text-base text-storyblackText ${selectedHowFoundOption?.label ? '' : 'opacity-50'}`}>
                  {selectedHowFoundOption?.label || 'Select'}
                </span>
              </div>
              <div className="pointer-events-none flex items-center px-1 text-gray-700">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </div>
            </div>

            {isDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="py-1">
                  {HOW_FOUND_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-left ${
                        howFoundUs === option.value ? 'bg-gray-50' : ''
                      }`}
                      onClick={() => {
                        setHowFoundUs(option.value);
                        setIsDropdownOpen(false);
                        // Reset other text when not selecting Other
                        if (option.value !== HowDidYouFindUsType.Other) {
                          setHowFoundUsOther('');
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {howFoundUs === HowDidYouFindUsType.Other && (
            <input
              type="text"
              value={howFoundUsOther}
              onChange={(e) => setHowFoundUsOther(e.target.value)}
              placeholder="How did you find us?"
              className="w-full mt-2 p-3 text-center border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="School name"
            />
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-8">
        <div>
          <BackButton onClick={handleBack}></BackButton>
        </div>

        <button
          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base w-auto justify-center ${
            isFormValid && !isSubmitting ? '' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={handleContinue}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Completing...' : 'Complete Setup'}
        </button>
      </div>
    </div>
  );
};

export default OnboardingHowDidYouFindUs;
